import { useEffect, useState } from 'react';
import { CARD_TEXT_MAX, countBlanks } from '@shared/rules';
import type { CardType, PackSummary } from '@shared/types';
import { api } from '../lib/api';
import { getName } from '../lib/storage';
import { usePrefs } from './Prefs';

/** Suggest a card for the official deck. Used on /suggest and from the in-game menu. */
export function SuggestForm({ roomCode, onDone }: { roomCode?: string; onDone?: () => void }) {
  const { toast } = usePrefs();
  const [type, setType] = useState<CardType>('answer');
  const [text, setText] = useState('');
  const [gloss, setGloss] = useState('');
  const [explanation, setExplanation] = useState('');
  const [packId, setPackId] = useState('');
  const [name, setName] = useState(getName());
  const [packs, setPacks] = useState<PackSummary[]>([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .packs()
      .then((p) => setPacks(p.filter((x) => x.kind === 'official')))
      .catch(() => {});
  }, []);

  const blanks = countBlanks(text.replace(/_{3,}/g, '___'));

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (text.trim().length < 2) return setError('Write the card first.');
    if (type === 'prompt' && blanks === 0 && !/\?\s*$/.test(text)) {
      return setError('Prompts need a blank (type ___) or should be a question.');
    }
    setBusy(true);
    try {
      await api.suggest({ type, text, gloss, explanation, packId: packId || null, submitterName: name, roomCode: roomCode ?? null });
      toast('Thanks! The CompleteMasti team will take a look.', 'success');
      setText('');
      setGloss('');
      setExplanation('');
      onDone?.();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="stack" onSubmit={submit}>
      <div className="segmented" role="group" aria-label="Card type">
        <button type="button" aria-pressed={type === 'answer'} onClick={() => setType('answer')}>
          Answer card
        </button>
        <button type="button" aria-pressed={type === 'prompt'} onClick={() => setType('prompt')}>
          Prompt card
        </button>
      </div>
      <label className="field">
        {type === 'prompt' ? 'Prompt' : 'Answer'}
        <span className="hint">
          {type === 'prompt'
            ? 'Use ___ (three underscores) for each blank. Example: "Mummy ji\'s cure for literally everything: ___."'
            : 'A noun phrase works best. Example: "Papa\'s 40-minute voice note"'}
        </span>
        <textarea className="input" style={{ minHeight: 80 }} maxLength={CARD_TEXT_MAX} value={text} onChange={(e) => setText(e.target.value)} required />
        {type === 'prompt' && blanks > 1 && <span className="hint">Players will pick {Math.min(blanks, 3)} cards for this one.</span>}
      </label>
      <label className="field">
        Translation <span className="hint">Optional. Short English meaning for any Hindi/Urdu/Punjabi words.</span>
        <input className="input" maxLength={200} value={gloss} onChange={(e) => setGloss(e.target.value)} />
      </label>
      <label className="field">
        Context <span className="hint">Optional. Which film, song, person or custom is this about?</span>
        <input className="input" maxLength={500} value={explanation} onChange={(e) => setExplanation(e.target.value)} />
      </label>
      <div className="grid-2">
        <label className="field">
          Best pack for it
          <select className="input" value={packId} onChange={(e) => setPackId(e.target.value)}>
            <option value="">Not sure</option>
            {packs.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          Your name <span className="hint">Optional</span>
          <input className="input" maxLength={40} value={name} onChange={(e) => setName(e.target.value)} />
        </label>
      </div>
      {error && <p className="error">{error}</p>}
      <button className="btn btn-primary btn-lg" disabled={busy}>
        {busy ? 'Sending...' : 'Send suggestion'}
      </button>
    </form>
  );
}
